/**
 * client/src/services/adaptiveGps.ts
 *
 * FIX GPS-ADAPTIVE (Set 2026) — Campionamento adattivo in base alla velocità.
 *
 * Stesse bande del fallback foreground (nativeGeolocation.intervalFromSpeedMps):
 *   > 80 km/h → 2000ms  (autostrada: serve fedeltà alta)
 *   40-80     → 5000ms
 *   10-40     → 10000ms (urbano)
 *   < 10      → 30000ms (fermo/traffico: risparmia batteria)
 *
 * PRIMA: il watcher BACKGROUND (@capacitor-community/background-geolocation)
 * usava un intervallo FISSO di 3s per sempre — niente adattamento alla
 * velocità, batteria bruciata anche da fermi, e il TrackingConfig
 * (intervalHighway/Fast/Urban/Idle) era solo un tipo mai applicato.
 *
 * ORA: un AdaptiveIntervalController alimentato da ogni fix GPS riconfigura
 * il watcher SOLO quando la banda di velocità cambia in modo stabile
 * (debounce 15s) e con un gap minimo tra riconfigurazioni (60s). La
 * riconfigurazione è GUARDED: se fallisce, il watcher precedente resta
 * attivo (o viene riavviato con l'ultimo intervallo buono) — il tracking
 * non si interrompe mai.
 */

export const ADAPTIVE_INTERVALS = {
  highway: 2000,
  fast: 5000,
  urban: 10000,
  idle: 30000,
} as const;

/** Intervallo target (ms) in base alla velocità in km/h. */
export function intervalFromSpeedKmh(
  speedKmh: number | null | undefined,
): number {
  if (speedKmh == null || Number.isNaN(speedKmh)) return ADAPTIVE_INTERVALS.idle;
  if (speedKmh > 80) return ADAPTIVE_INTERVALS.highway;
  if (speedKmh >= 40) return ADAPTIVE_INTERVALS.fast;
  if (speedKmh >= 10) return ADAPTIVE_INTERVALS.urban;
  return ADAPTIVE_INTERVALS.idle;
}

export interface AdaptiveIntervalControllerOptions {
  /** Persistenza della banda prima di riconfigurare (default 15s). */
  debounceMs?: number;
  /** Gap minimo tra due riconfigurazioni (default 60s). */
  minGapMs?: number;
  /** Clock iniettabile per i test (default Date.now). */
  now?: () => number;
  /** Callback di applicazione (la riconfigurazione del watcher). */
  apply: (intervalMs: number) => void;
}

/**
 * Controller con isteresi: riconfigura il watcher solo se la banda target
 * è diversa da quella corrente E resta stabile per `debounceMs`, rispettando
 * il gap minimo tra riconfigurazioni.
 */
export class AdaptiveIntervalController {
  private currentIntervalMs: number;
  private pending: { intervalMs: number; since: number } | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastReconfigureAt = 0;
  private readonly debounceMs: number;
  private readonly minGapMs: number;
  private readonly now: () => number;
  private readonly apply: (intervalMs: number) => void;

  constructor(options: AdaptiveIntervalControllerOptions & { initialIntervalMs: number }) {
    this.currentIntervalMs = options.initialIntervalMs;
    this.debounceMs = options.debounceMs ?? 15_000;
    this.minGapMs = options.minGapMs ?? 60_000;
    this.now = options.now ?? Date.now;
    this.apply = options.apply;
  }

  get current(): number {
    return this.currentIntervalMs;
  }

  /** Chiamato a ogni fix GPS con la velocità in km/h. */
  onFix(speedKmh: number | null | undefined): void {
    const target = intervalFromSpeedKmh(speedKmh);
    if (target === this.currentIntervalMs) {
      this.clearPending();
      return;
    }

    const now = this.now();
    if (this.pending && this.pending.intervalMs === target) {
      // Banda già pendente: se stabile oltre il debounce e oltre il gap → applica
      if (
        now - this.pending.since >= this.debounceMs &&
        now - this.lastReconfigureAt >= this.minGapMs
      ) {
        this.applyNow(target);
      }
      return;
    }

    // Nuova banda target: ricomincia il debounce
    this.pending = { intervalMs: target, since: now };
    this.scheduleDebounce(target);
  }

  private scheduleDebounce(target: number): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.now() - this.lastReconfigureAt >= this.minGapMs) {
        this.applyNow(target);
      }
    }, this.debounceMs);
    (this.debounceTimer as any).unref?.();
  }

  private applyNow(intervalMs: number): void {
    this.currentIntervalMs = intervalMs;
    this.lastReconfigureAt = this.now();
    this.pending = null;
    this.apply(intervalMs);
  }

  private clearPending(): void {
    this.pending = null;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  dispose(): void {
    this.clearPending();
  }
}
